import { useState } from 'react';
import { Survey } from 'posthog-js';
import useCaptureSurveyShownEvent from 'src/components/UserSurvey/hooks/useCaptureSurveyShownEvent';
import UserSurveyDrawer from 'src/components/UserSurvey/UserSurveyDrawer';
import UserSurveyToast from 'src/components/UserSurvey/UserSurveyToast';
import {
	storeHasSeenSurvey,
	getPostHogSurveyPageTitle,
} from 'src/components/UserSurvey/utils';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import useModal from 'src/components/Modals/hooks/useModal';
import UserSurveyContent from 'src/components/UserSurvey/UserSurveyContent/UserSurveyContent';
import { UserSurveyContentProps } from 'src/components/UserSurvey/types';
import useNotificationStore from 'src/stores/useNotificationStore';

export interface UserSurveyProps {
	survey: Survey;
}

export default function UserSurvey({ survey }: UserSurveyProps) {
	const isMobileScreenSize = useIsMobileScreenSize();
	const { captureEvent } = usePostHogCapture();
	const notificationStore = useNotificationStore();
	const { hide } = useModal();

	useCaptureSurveyShownEvent(survey.id);

	const [currentPage, setCurrentPage] = useState(0);

	const { closeDrawer, isDrawerClosing } = useCloseBottomDrawer({
		onClose: hide,
	});

	const closeSurvey: UserSurveyContentProps['closeSurvey'] = ({
		sendDismissedEvent,
	} = {}) => {
		if (sendDismissedEvent) {
			captureEvent('survey dismissed', { $survey_id: survey.id });
		}

		isMobileScreenSize ? closeDrawer() : hide();
	};

	const closeFromXButtonClick = () => {
		// Store a flag in `localStorage` to prevent the survey from being shown again.
		storeHasSeenSurvey(survey.id);

		closeSurvey({
			// If the user is on the last page, their survey answers have already
			// been sent and they're simply viewing a "thank you" page. So only
			// send a dismissed event if they haven't reached that point.
			sendDismissedEvent: currentPage !== survey.questions.length + 1,
		});
	};

	const title = getPostHogSurveyPageTitle(survey, currentPage);

	const content = (
		<UserSurveyContent
			survey={survey}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			closeSurvey={closeSurvey}
		/>
	);

	// The survey toast isn't a _real_ toast so it doesn't interact correctly with
	// other toasts (i.e. notifications) when rendered together. So for now we hide
	// the survey whenever a notification is visible (applies to desktop only).
	const isNotificationVisible = notificationStore.notifications.some(
		(n) => !n.hidden
	);

	if (!isMobileScreenSize && isNotificationVisible) {
		return null;
	}

	if (isMobileScreenSize) {
		return (
			<UserSurveyDrawer
				title={title}
				onClose={closeFromXButtonClick}
				isClosing={isDrawerClosing}
			>
				{content}
			</UserSurveyDrawer>
		);
	}

	return (
		<UserSurveyToast title={title} onClose={closeFromXButtonClick}>
			{content}
		</UserSurveyToast>
	);
}
